import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { FilterFields } from '../filter-fields/filter-fields';
import { Router } from '@angular/router';
import { StatusLead } from '../../../interfaces/status-lead.interface';
import { TrackClickDirective } from '../../../directives/track-click.directive';
import { runtimeLogger } from '../../../utils/runtime-logger.util';
import { empty } from 'rxjs';
import { availableOriginsType } from '../filter-fields/models/filter-field.model';

export interface FilterConfig {
  searchFields?: string[];
  alphabeticalField?: string;
  filterFields?: {
    renewal?: string;
    preAprovado?: string;
    origin?: string;
    statusLead?: StatusLead[];
    selectedStatusLeadIds?: number[];
  };
  originsList?: string[];
  dateField?: string;
  availableFilters?: ('renewal' | 'preAprovado')[];
}

export interface LeadColumn {
  key: string;
  label: string;
  type?: 'text' | 'status' | 'action' | 'icon' | 'switch' | 'action';
  filterable?: boolean;
  actionLabel?: string;
  truncate?: boolean;
}

export interface LeadRow {
  [key: string]: any;
  status?: string;
  idLead?: number;
}

export interface ServerSidePagination {
  totalRecords: number;
  page: number;
  pageSize: number;
  from: number;
  to: number;
}

export interface ServerSidePaginationInterface extends ServerSidePagination { }

interface SortState {
  field: string;
  order: 'asc' | 'desc' | null;
}

export interface ViewButtonTracking {
  trackEvent?: string;
  trackCategory?: string;
  trackLabel?: string;
}

@Component({
  selector: 'app-leads-table',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatButtonModule, MatProgressSpinnerModule,
    MatSlideToggleModule, FormsModule, FilterFields, TrackClickDirective],
  templateUrl: './leads-table.html',
  styleUrls: ['./leads-table.scss']
})
export class LeadsTable implements OnInit, OnChanges {
  @Input() columns: LeadColumn[] = [];
  @Input() data: LeadRow[] = [];
  @Input() filterConfig: FilterConfig = {};
  @Input() viewButtonRoute: string = '';
  @Input() serverSidePagination?: ServerSidePagination;
  @Input() loading: boolean = false;
  @Input() emptyMessage: string = 'Nenhum dado encontrado';
  @Input() errorMessage: string = '';
  @Input() filterMode: 'default' | 'admin' = 'default';
  @Input() adminFilterOptions: { value: string, label: string }[] = [];
  @Input() actionButtonLabel: string = 'Visualizar';
  @Input() sortableColumns: string[] = [];
  @Input() toggleSortOnly: boolean = false;
  @Input() priorityField: string = '';
  @Input() priorityIds: number[] = [];
  @Input() showStatusIcon: boolean = true;
  @Input() viewButtonTracking: ViewButtonTracking = {
    trackEvent: 'table_button_click',
    trackCategory: 'leads_table',
    trackLabel: 'view_lead'
  };
  @Input() availableOrigins: availableOriginsType[] = ['sempre-novo', 'ofertas-vw', 'campanha', 'S&D-PF', 'S&D-PJ', 'S&D-CONCESSIONARIOS', 'S&D-COBRANDED'];
  @Input() hasFilterTrigger: boolean = true;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() viewLeadClick = new EventEmitter<LeadRow>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() filterChange = new EventEmitter<string[]>();
  @Output() filterStatusLeadChange = new EventEmitter<number[]>();
  @Output() dateRangeChange = new EventEmitter<{ start: string, end: string }>();
  @Output() clearAllFilters = new EventEmitter<void>();
  @Output() sortChange = new EventEmitter<{ field: string, order: 'ASC' | 'DESC' }>();
  @Output() switchChange = new EventEmitter<{ row: LeadRow, field: string, value: boolean }>();
  @Output() actionClick = new EventEmitter<{ row: LeadRow, action: string }>();
  @Output() adminSearchChange = new EventEmitter<string>();

  searchText: string = '';
  selectedFilters: string[] = [];
  selectedStatusLeadIds: number[] = [];
  dateRange: { start: string, end: string } = { start: '', end: '' };

  sortState: SortState = { field: '', order: null };

  constructor(private router: Router) {
    this.selectedStatusLeadIds = this.filterConfig.filterFields?.selectedStatusLeadIds ?? [];
  }

  filteredData: LeadRow[] = [];
  currentPage: number = 0;
  pageSize: number = 5;

  ngOnInit() {
    this.selectedStatusLeadIds = this.filterConfig.filterFields?.selectedStatusLeadIds ?? [];
    this.applyFilters();
  }

  ngOnChanges() {
    this.selectedStatusLeadIds = this.filterConfig.filterFields?.selectedStatusLeadIds ?? [];
    this.applyFilters();
  }

  getTrackingEvent(row: LeadRow): string {
    return this.viewButtonTracking.trackEvent || 'table_button_click';
  }

  getTrackingCategory(): string {
    return this.viewButtonTracking.trackCategory || 'leads_table';
  }

  getTrackingLabel(row: LeadRow): string {
    // Simplesmente usa o label configurado
    return this.viewButtonTracking.trackLabel || 'view_lead';
  }

  getTrackingValue(row: LeadRow): number | undefined {
    // Valor fixo sempre 1
    return 1;
  }
  get filteredPaginatedData(): LeadRow[] {
    if (this.serverSidePagination) {
      return this.data;
    }
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredData.slice(startIndex, endIndex);
  }

  get totalItems(): number {
    if (this.serverSidePagination) {
      return this.serverSidePagination.totalRecords;
    }
    return this.filteredData.length;
  }

  get displayedColumns(): string[] {
    return [
      ...(this.showStatusIcon ? ['statusIcon'] : []),
      ...this.columns.map(col => col.key),
      'actions'
    ]
  }

  onSearchChange(searchText: string): void {
    this.searchText = searchText;
    if (this.serverSidePagination) {
      this.searchChange.emit(searchText);
    } else {
      this.applyFilters();
    }
  }

  onAdminSearchChange(searchValue: string): void {
    const normalizedValue = searchValue === 'todas' ? '' : searchValue;
    this.searchText = normalizedValue;

    if (this.serverSidePagination || this.filterMode === 'admin') {
      this.adminSearchChange.emit(normalizedValue);
    }

    if (!this.serverSidePagination) {
      this.applyFilters();
    }
  }

  onOrderChange(order: 'A-Z' | 'Z-A' | null): void {
    if (this.filterConfig.alphabeticalField) {
      if (order === 'A-Z') {
        this.sortState.field = this.filterConfig.alphabeticalField;
        this.sortState.order = 'asc';
      } else if (order === 'Z-A') {
        this.sortState.field = this.filterConfig.alphabeticalField;
        this.sortState.order = 'desc';
      } else {
        this.sortState.field = '';
        this.sortState.order = null;
      }

      if (this.serverSidePagination && this.sortState.field && this.sortState.order) {
        this.sortChange.emit({
          field: this.sortState.field,
          order: this.sortState.order === 'asc' ? 'ASC' : 'DESC'
        });
      } else {
        this.applyFilters();
      }
    }
  }

  onFilterChange(filters: string[]): void {
    this.selectedFilters = filters;
    if (this.serverSidePagination) {
      this.filterChange.emit(filters);
    } else {
      this.applyFilters();
    }
  }

  onFilterStatusLeadChange(filters: number[]): void {
    this.selectedStatusLeadIds = filters;
    this.filterStatusLeadChange.emit(filters);
  }

  onDateRangeChange(dateRange: { start: string, end: string }): void {
    runtimeLogger.debug('LeadsTable - onDateRangeChange recebido:', dateRange);
    this.dateRange = dateRange;
    if (this.dateRange.start && this.dateRange.end) {
      if (this.serverSidePagination || this.filterMode === 'admin') {
        runtimeLogger.debug('LeadsTable - Emitindo dateRangeChange para parent');
        this.dateRangeChange.emit(dateRange);
      }

      if (!this.serverSidePagination) {
        this.applyFilters();
      }
    }
  }

  onClearAllFilters(): void {
    this.searchText = '';
    this.selectedFilters = [];
    this.selectedStatusLeadIds = [];
    this.dateRange = { start: '', end: '' };
    this.sortState = { field: '', order: null };
    this.currentPage = 0;

    if (this.serverSidePagination || this.filterMode === 'admin') {
      this.clearAllFilters.emit();
    }

    if (!this.serverSidePagination) {
      this.applyFilters();
    }
  }

  onSwitchChange(row: LeadRow, field: string, event: any): void {
    if (this.isSwitchDisabled(row, field)) {
      return;
    }

    const isChecked = event.checked;
    row[field] = isChecked;
    this.switchChange.emit({ row, field, value: isChecked });
  }

  isSwitchDisabled(row: LeadRow, field: string): boolean {
    return !!row[`${field}Disabled`];
  }

  onActionClick(row: LeadRow, action: string): void {
    if (this.isActionDisabled(row, action)) {
      return;
    }

    this.actionClick.emit({ row, action });
  }

  isActionDisabled(row: LeadRow, action: string): boolean {
    return !!row[`${action}Disabled`];
  }

  getSwitchState(row: LeadRow, field: string): boolean {
    const value = row[field];
    if (typeof value === 'string') {
      return value.toLowerCase() === 'ativo' || value.toLowerCase() === 'true' || value === '1';
    }
    return !!value;
  }

  private applyFilters(): void {
    if (this.serverSidePagination || this.filterMode === 'admin') {
      this.filteredData = [...this.data];
      return;
    }

    let filtered = [...this.data];

    if (this.searchText) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(row => {
        if (this.filterConfig.searchFields && this.filterConfig.searchFields.length > 0) {
          return this.filterConfig.searchFields.some(field => {
            const value = row[field];
            return value && value.toString().toLowerCase().includes(searchLower);
          });
        }
        return this.columns.some(column => {
          const value = row[column.key];
          return value && value.toString().toLowerCase().includes(searchLower);
        });
      });
    }

    if (this.selectedFilters.length > 0) {
      filtered = filtered.filter(row => {
        return this.selectedFilters.every(filter => {
          switch (filter) {
            case 'Renewal':
              const renewalField = this.filterConfig.filterFields?.renewal || 'renovaMais';
              return row[renewalField] === true;
            case 'Pré-Aprovado':
              const preAprovadoField = this.filterConfig.filterFields?.preAprovado || 'preAprovado';
              return row[preAprovadoField] === 'S';
            default:
              return false;
          }
        });
      });
    }

    if (this.selectedStatusLeadIds.length > 0) {
      filtered = filtered.filter(item => {
        return this.selectedStatusLeadIds.every(filter => {
          return item['statusId'] === filter;
        });
      });
    }

    if (this.dateRange.start || this.dateRange.end) {
      const startDate = this.dateRange.start ? new Date(this.dateRange.start) : null;
      const endDate = this.dateRange.end ? new Date(this.dateRange.end) : null;

      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(23, 59, 59, 999);

      const dateField = this.filterConfig.dateField || 'disponivelDesde';
      filtered = filtered.filter(row => {
        const rowDate = this.parseDate(row[dateField]);
        if (!rowDate) return false;

        if (startDate && rowDate < startDate) return false;
        if (endDate && rowDate > endDate) return false;

        return true;
      });
    }

    if (this.sortState.field && this.sortState.order) {
      filtered.sort((a, b) => this.compareRowsForSort(a, b));
    }

    this.filteredData = filtered;
    this.currentPage = 0;
  }

  dateColumns: string[] = ['dataSimulacao', 'ultimoContato', 'dataProposta', 'dataTerminoContrato', 'disponivelDesde', 'retornarEm', 'dataInicio', 'dataTermino', 'contemplacao'];

  isDateColumn(columnKey: string): boolean {
    return this.dateColumns.includes(columnKey);
  }

  toggleSortColumns: any = {
    'nome': 'NM_NOME',
    'origem': 'ORIGEM',
    'status': 'STATUS_LEAD',
    'numeroDaProposta': 'CD_PROP_CONTR_COT',
    'statusContrato': 'DS_STATUS_CONTR_COT',
    'tipoCampanha': 'NM_CAMPANHA',
    'dataSimulacao': 'DATA_SIMULACAO',
    'ultimoContato': 'DT_CONTATO',
    'dataProposta': 'DT_PROPOSTA',
    'dataTerminoContrato': 'DT_CONTR_FIM',
    'disponivelDesde': 'DT_CADASTRO',
    'retornarEm': 'RETORNAR_EM',
    'contemplacao': 'COMTEMPLACAO'
  };

  isToggleSortColumn(columnKey: string): boolean {
    if (this.sortableColumns.length > 0) {
      return this.sortableColumns.includes(columnKey);
    }

    return Object.keys(this.toggleSortColumns).includes(columnKey);
  }

  getCurrentSortOrder(columnKey: string): 'asc' | 'desc' | null {
    return this.sortState.field === columnKey ? this.sortState.order : null;
  }

  toggleSortOrder(columnKey: string): void {
    if (!this.isToggleSortColumn(columnKey) && columnKey !== this.filterConfig.alphabeticalField) return;

    if (this.toggleSortOnly && this.isToggleSortColumn(columnKey)) {
      if (this.sortState.field === columnKey) {
        this.sortState.order = this.sortState.order === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortState.field = columnKey;
        this.sortState.order = 'asc';
      }
    } else {
      if (this.sortState.field === columnKey) {
        if (this.sortState.order === 'asc') {
          this.sortState.order = 'desc';
        } else if (this.sortState.order === 'desc') {
          this.sortState.order = null;
          this.sortState.field = '';
        } else {
          this.sortState.order = 'asc';
        }
      } else {
        this.sortState.field = columnKey;
        this.sortState.order = 'asc';
      }
    }

    if (this.serverSidePagination && this.sortState.field && this.sortState.order) {
      this.sortChange.emit({
        field: this.toggleSortColumns[this.sortState.field],
        order: this.sortState.order === 'asc' ? 'ASC' : 'DESC'
      });
    } else {
      this.applyFilters();
    }
  }

  private compareRowsForSort(rowA: LeadRow, rowB: LeadRow): number {
    const adminPriorityComparison = this.compareAdminCampaignPriority(rowA, rowB);

    if (adminPriorityComparison !== 0) {
      return adminPriorityComparison;
    }

    const fieldComparison = this.compareFieldValues(rowA, rowB, this.sortState.field);
    return this.sortState.order === 'asc' ? fieldComparison : -fieldComparison;
  }

  private compareAdminCampaignPriority(rowA: LeadRow, rowB: LeadRow): number {
    if (!this.priorityField || this.priorityIds.length === 0) {
      return 0;
    }

    const typePriorityA = this.getCampaignTypePriority(Number(rowA[this.priorityField]));
    const typePriorityB = this.getCampaignTypePriority(Number(rowB[this.priorityField]));

    return typePriorityA - typePriorityB;
  }

  private getCampaignTypePriority(id: number): number {
    const priorityIndex = this.priorityIds.indexOf(id);
    return priorityIndex >= 0 ? priorityIndex : this.priorityIds.length;
  }

  private compareFieldValues(rowA: LeadRow, rowB: LeadRow, field: string): number {
    const valueA = rowA[field];
    const valueB = rowB[field];

    if (this.isDateColumn(field)) {
      const dateA = this.parseDate(valueA);
      const dateB = this.parseDate(valueB);

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return dateA.getTime() - dateB.getTime();
    }

    if (field === 'statusCampanha') {
      const statusA = this.getSwitchState(rowA, field) ? 'ativo' : 'inativo';
      const statusB = this.getSwitchState(rowB, field) ? 'ativo' : 'inativo';
      return statusA.localeCompare(statusB, 'pt-BR', { sensitivity: 'base' });
    }

    const normalizedValueA = valueA?.toString().trim().toLowerCase() || '';
    const normalizedValueB = valueB?.toString().trim().toLowerCase() || '';

    return normalizedValueA.localeCompare(normalizedValueB, 'pt-BR', {
      numeric: true,
      sensitivity: 'base'
    });
  }

  formatCellValue(columnKey: string, value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (this.isDateColumn(columnKey)) {
      return this.formatDate(value);
    }

    return String(value);
  }

  private formatDate(dateString: string): string {
    if (!dateString || dateString === 'null' || dateString === 'undefined') {
      return '-';
    }

    const dateStr = String(dateString).trim();

    if (!dateStr || dateStr === '-') {
      return '-';
    }

    if (dateStr.includes('/')) {
      return dateStr;
    }

    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
    }

    return dateStr || '-';
  }

  private parseDate(dateString: string): Date | null {
    if (!dateString) return null;

    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    if (dateString.includes('-')) {
      return new Date(dateString);
    }

    return null;
  }

  getAvailableFilters(): ('renovaMais' | 'preAprovado' | 'origem' | 'statusLead')[] {
    if (this.filterMode === 'admin') {
      return [];
    }

    const available: ('renovaMais' | 'preAprovado' | 'origem' | 'statusLead')[] = [];

    if (this.filterConfig.filterFields?.renewal) {
      available.push('renovaMais');
    }

    if (this.filterConfig.filterFields?.preAprovado) {
      available.push('preAprovado');
    }

    if (this.filterConfig.filterFields?.origin) {
      available.push('origem');
    }

    if (this.filterConfig.filterFields?.statusLead) {
      available.push('statusLead');
    }

    return available;
  }

  getStatusLeadFilters(): StatusLead[] | undefined {
    if (this.filterMode === 'admin') {
      return undefined;
    }
    return this.filterConfig.filterFields?.statusLead;
  }

  getSelectedStatusLeadIdsFilters(): number[] {
    if (this.filterMode === 'admin') {
      return [];
    }
    return this.filterConfig.filterFields?.selectedStatusLeadIds ?? [];
  }

  getDisplayedRange(): string {
    if (this.serverSidePagination) {
      const { from, to, totalRecords } = this.serverSidePagination;
      return `${from}-${to} de ${totalRecords}`;
    }
    if (this.totalItems === 0) return '0-0 de 0';
    const start = this.currentPage * this.pageSize + 1;
    const end = Math.min((this.currentPage + 1) * this.pageSize, this.totalItems);
    return `${start}-${end} de ${this.totalItems}`;
  }

  onPageSizeChange(event: any): void {
    const newPageSize = +event.target.value;
    this.pageSize = newPageSize;
    this.currentPage = 0;

    if (this.serverSidePagination) {
      this.pageSizeChange.emit(newPageSize);
    }
  }

  previousPage(): void {
    if (this.serverSidePagination) {
      const newPage = this.serverSidePagination.page - 1;
      if (newPage >= 1) {
        this.pageChange.emit(newPage);
      }
    } else {
      if (this.currentPage > 0) this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.serverSidePagination) {
      const newPage = this.serverSidePagination.page + 1;
      if (this.serverSidePagination.to < this.serverSidePagination.totalRecords) {
        this.pageChange.emit(newPage);
      }
    } else {
      if ((this.currentPage + 1) * this.pageSize < this.totalItems) this.currentPage++;
    }
  }

  hasPreviousPage(): boolean {
    if (this.serverSidePagination) {
      return this.serverSidePagination.page > 1;
    }
    return this.currentPage > 0;
  }

  hasNextPage(): boolean {
    if (this.serverSidePagination) {
      return this.serverSidePagination.to < this.serverSidePagination.totalRecords;
    }
    return (this.currentPage + 1) * this.pageSize < this.totalItems;
  }

  getStatusColor(status: string): string {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'novo/pendente': return '#FFD600';
      case 'pendente/novo': return '#FFD600';
      case 'em negociação': return '#4CAF50';
      case 'negociação': return '#4CAF50';
      default: return '#BDBDBD';
    }
  }

  formatStatus(status: string): string {
    const statusLower = (status || '').toLowerCase();
    const statusMap: { [key: string]: string } = {
      'negociação': 'Em Negociação',
      'novo/pendente': 'Novo/Pendente',
      'pendente/novo': 'Novo/Pendente',
      'em negociação': 'Em Negociação'
    };
    return statusMap[statusLower] || status;
  }

  getPreApprovedIcon(preAprovacao: string): string[] {
    if (!preAprovacao) return [];
    switch (preAprovacao.toString().toLowerCase()) {
      case 'sim': return ['assets/images/preaproved.png'];
      case 'necessita análise': return ['assets/images/preAprovedYellow.png'];
      case 'não solicitado': return ['assets/images/pre_aprovado_cinza.png'];
      default: return [];
    }
  }

  getRenewalIcon(renewal: string): string[] {
    if (!renewal) return [];
    switch (renewal.toString().toLowerCase()) {
      case 'renewalblue': return ['assets/images/renewalBlueBig.png'];
      case 'renewalgrey': return ['assets/images/renewalGray.png'];
      default: return [];
    }
  }

  onViewButtonClick(row: LeadRow): void {
    if (row['idLead']) {
      this.viewLeadClick.emit(row);
    } else if (this.viewButtonRoute) {
      this.router.navigate([this.viewButtonRoute]);
    }
  }
}
